// RevenueGenerationChart.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Paper, ToggleButtonGroup, ToggleButton } from '@mui/material';
import { Bar } from 'react-chartjs-2';
import { apiFetch } from '../../utils/api';
import { CURRENT_YEAR, CURRENT_QUARTER } from '../../constants';
import { Logger } from '../../utils/logger';

const logger = new Logger('RevenueGenerationChart');

/**
 * A dedicated component for displaying revenue generation data with time frame toggles.
 * This component handles its own data fetching and state management.
 * 
 * @param {Object} props - Component props
 * @param {Object} props.employee - Employee object containing employee_id
 * @param {boolean} props.hideTitle - Whether to hide the title
 * @param {boolean} props.compact - Whether to display in compact mode
 * @param {boolean} props.hideToggle - Whether to hide the time frame toggle
 * @param {string} props.timeFrame - Time frame to use (if controlled externally)
 */
const RevenueGenerationChart = ({ 
  employee, 
  hideTitle = false, 
  compact = false,
  hideToggle = false,
  timeFrame: externalTimeFrame = null
}) => {
  // State for time frame toggle and data
  const [internalTimeFrame, setInternalTimeFrame] = useState('current_year');
  
  // Use external timeFrame if provided, otherwise use internal state
  const timeFrame = externalTimeFrame || internalTimeFrame;
  const [revenueData, setRevenueData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [title, setTitle] = useState('Current Year Revenue Generated');
  
  // Format currency for display
  const formatCurrency = (value) => {
    if (value === undefined || value === null) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };
  
  // Calculate total revenue
  const calculateTotalRevenue = (data) => {
    return data.reduce((sum, item) => sum + item.amount, 0);
  };
  
  // Fetch revenue data based on time frame
  const fetchRevenueData = useCallback(async () => {
    if (!employee?.employee_id) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      logger.debug(`Fetching financial impact data with time_frame=${timeFrame}`);
      
      // Make authenticated API call
      const response = await apiFetch(
        `employees/${employee.employee_id}/financial-impact?period_type=quarterly&time_frame=${timeFrame}`
      );
      const responseData = await response.json();

      // Extract revenue generation data
      const data = responseData?.revenue_generation_data?.quarterly || responseData?.revenue_generation || [];

      // If no data at all, surface the empty state rather than rendering zero bars
      if (data.length === 0) {
        setRevenueData([]);
        const titleMap = {
          current_year: 'Current Year Revenue Generated',
          last_4_quarters: 'Last 4 Quarters Revenue Generated',
          last_year: 'Previous Year Revenue Generated',
        };
        setTitle(titleMap[timeFrame] || 'Revenue Generated');
        setIsLoading(false);
        return;
      }

      // Process data based on time frame
      let processedData = [];
      let chartTitle = '';

      switch (timeFrame) {
        case 'current_year':
          // Use the constant for current year
          const currentYear = CURRENT_YEAR;
          
          // Q1 → Q4 left to right (oldest to newest)
          const currentYearQuarters = [
            { period: `Q1 ${currentYear}`, amount: 0 },
            { period: `Q2 ${currentYear}`, amount: 0 },
            { period: `Q3 ${currentYear}`, amount: 0 },
            { period: `Q4 ${currentYear}`, amount: 0 }
          ];

          data.forEach(item => {
            if (item.period.includes(currentYear.toString())) {
              const quarterMatch = item.period.match(/Q(\d)/);
              if (quarterMatch) {
                const quarterIndex = parseInt(quarterMatch[1]) - 1;
                if (quarterIndex >= 0 && quarterIndex < 4) {
                  currentYearQuarters[quarterIndex].amount = item.amount;
                }
              }
            }
          });

          processedData = currentYearQuarters;
          chartTitle = 'Current Year Revenue Generated';
          break;

        case 'last_4_quarters':
          const currentQuarter = CURRENT_QUARTER;
          const year = CURRENT_YEAR;

          const chronologicalQuarters = [];
          for (let i = 3; i >= 0; i--) {
            let q = currentQuarter - i;
            let y = year;
            if (q <= 0) { q += 4; y -= 1; }
            chronologicalQuarters.push({ period: `Q${q} ${y}`, amount: 0 });
          }

          data.forEach(item => {
            const index = chronologicalQuarters.findIndex(q => q.period === item.period);
            if (index !== -1) chronologicalQuarters[index].amount = item.amount;
          });

          processedData = chronologicalQuarters;
          chartTitle = 'Last 4 Quarters Revenue Generated';
          break;

        case 'last_year':
          const lastYear = CURRENT_YEAR - 1;

          const lastYearQuarters = [
            { period: `Q1 ${lastYear}`, amount: 0 },
            { period: `Q2 ${lastYear}`, amount: 0 },
            { period: `Q3 ${lastYear}`, amount: 0 },
            { period: `Q4 ${lastYear}`, amount: 0 }
          ];

          data.forEach(item => {
            if (item.period.includes(lastYear.toString())) {
              const quarterMatch = item.period.match(/Q(\d)/);
              if (quarterMatch) {
                const quarterIndex = parseInt(quarterMatch[1]) - 1;
                if (quarterIndex >= 0 && quarterIndex < 4) {
                  lastYearQuarters[quarterIndex].amount = item.amount;
                }
              }
            }
          });

          processedData = lastYearQuarters;
          chartTitle = 'Previous Year Revenue Generated';
          break;
          
        default:
          processedData = data.slice(0, 4); // Limit to 4 items
          chartTitle = 'Revenue Generated';
      }
      
      setRevenueData(processedData);
      setTitle(chartTitle);
      setIsLoading(false);
      
      logger.debug('Processed revenue data:', processedData);
    } catch (error) {
      logger.error('Error fetching financial impact data:', error);
      setError('Failed to load revenue data');
      setIsLoading(false);
    }
  }, [employee, timeFrame]);
  
  // Fetch data when employee or time frame changes
  useEffect(() => {
    fetchRevenueData();
  }, [employee, timeFrame, fetchRevenueData]);
  
  // Handle time frame change
  const handleTimeFrameChange = (event, newTimeFrame) => {
    if (newTimeFrame !== null) {
      setInternalTimeFrame(newTimeFrame);
    }
  };
  
  // Chart data
  const chartData = {
    labels: revenueData.map(item => item.period),
    datasets: [
      {
        label: 'Revenue',
        data: revenueData.map(item => item.amount),
        backgroundColor: '#03DAC6',
        borderRadius: 4
      }
    ]
  };
  
  // Chart options
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: '#444',
          borderDash: [3, 3]
        },
        ticks: {
          color: '#aaa',
          font: {
            size: 12
          },
          callback: function(value) {
            return `$${(value / 1000).toFixed(0)}k`;
          }
        }
      },
      x: {
        grid: {
          color: '#444',
          borderDash: [3, 3]
        },
        ticks: {
          color: '#aaa',
          font: {
            size: 12
          }
        }
      }
    },
    plugins: {
      tooltip: {
        backgroundColor: '#333',
        borderColor: '#555',
        borderWidth: 1,
        titleColor: '#fff',
        bodyColor: '#03DAC6',
        cornerRadius: 4,
        padding: 10,
        callbacks: {
          label: function(context) {
            return `Revenue: ${formatCurrency(context.raw)}`;
          }
        }
      },
      legend: {
        display: false
      }
    }
  };
  
  // Calculate total revenue
  const totalRevenue = calculateTotalRevenue(revenueData);
  
  // Adjust chart options for compact mode
  if (compact) {
    chartOptions.scales.x.ticks.font.size = 10;
    chartOptions.scales.y.ticks.font.size = 10;
    chartOptions.plugins.tooltip.titleFont = { size: 10 };
    chartOptions.plugins.tooltip.bodyFont = { size: 10 };
    chartOptions.plugins.tooltip.padding = 6;
  }

  return (
    <Box>
      {/* Toggle buttons - hidden in compact mode or when hideToggle is true */}
      {!compact && !hideToggle && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <ToggleButtonGroup
            size="small"
            value={timeFrame}
            exclusive
            onChange={handleTimeFrameChange}
            aria-label="time frame"
          >
            <ToggleButton value="current_year" aria-label="current_year">
              Current Year
            </ToggleButton>
            <ToggleButton value="last_4_quarters" aria-label="last_4_quarters">
              Last 4 Quarters
            </ToggleButton>
            <ToggleButton value="last_year" aria-label="last_year">
              Last Year
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      )}
      
      {/* Summary Card - simplified in compact mode */}
      {!compact ? (
        <Box sx={{ mb: 3 }}>
          <Paper 
            elevation={0}
            sx={{ 
              p: 2, 
              bgcolor: 'background.default',
              border: '1px solid',
              borderColor: 'divider',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Typography variant="subtitle2" color="text.secondary" align="center">
              {title}
            </Typography>
            <Typography variant="h4" sx={{ mt: 1, color: '#03DAC6' }}>
              {formatCurrency(totalRevenue)}
            </Typography>
          </Paper>
        </Box>
      ) : (
        <Box sx={{ mb: 1 }}>
          <Typography variant="subtitle2" color="text.secondary" align="center">
            Revenue: {formatCurrency(totalRevenue)}
          </Typography>
        </Box>
      )}
      
      {/* Revenue Generation Chart */}
      <Paper 
        elevation={0}
        sx={{ 
          p: compact ? 1 : 2, 
          mb: compact ? 1 : 3,
          bgcolor: 'background.default',
          border: '1px solid',
          borderColor: 'divider'
        }}
      >
        {!compact && (
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
            Revenue Generation
          </Typography>
        )}
        
        {isLoading ? (
          <Typography variant="body2" color="text.secondary" align="center">
            Loading revenue data...
          </Typography>
        ) : error ? (
          <Typography variant="body2" color="error" align="center">
            {error}
          </Typography>
        ) : revenueData.length > 0 ? (
          <Box sx={{ height: compact ? 240 : 300 }}>
            <Bar data={chartData} options={chartOptions} />
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary" align="center">
            No revenue data available
          </Typography>
        )}
      </Paper>
    </Box>
  );
};

export default RevenueGenerationChart;
